import { motion } from 'framer-motion';
import { CalendarCheck } from 'lucide-react';
import { DropdownSelect, type DropdownOption } from '../../../components';
import { cn } from '../../../lib/cn';
import { springQuick, tapScale } from '../../../tokens';
import { StepLayout } from '../StepLayout';
import type { OnboardingDraft } from '../types';

type Props = {
  draft: OnboardingDraft;
  setDraft: (patch: Partial<OnboardingDraft>) => void;
  onContinue: () => void;
  canContinue: boolean;
};

const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DOW_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const dayOfMonthOptions: DropdownOption[] = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: ordinal(i + 1),
}));

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function PayDayStep({
  draft,
  setDraft,
  onContinue,
  canContinue,
}: Props) {
  const description =
    draft.payCycle === 'weekly'
      ? 'Pick the day your paycheck typically lands.'
      : draft.payCycle === 'biweekly'
        ? 'Pick a recent payday. We use it to anchor your 14-day cycle.'
        : draft.payCycle === 'semimonthly'
          ? 'Pick the two days you get paid each month.'
          : 'Pick the day of the month your paycheck lands.';

  return (
    <StepLayout
      icon={<CalendarCheck size={28} strokeWidth={1.5} />}
      title="When do you get paid?"
      description={description}
      onContinue={onContinue}
      canContinue={canContinue}
    >
      {draft.payCycle === 'weekly' ? (
        <WeeklyPicker
          value={draft.payDay}
          onChange={(d) => setDraft({ payDay: d })}
        />
      ) : null}

      {draft.payCycle === 'biweekly' ? (
        <BiweeklyPicker
          value={draft.payAnchor}
          onChange={(iso) => setDraft({ payAnchor: iso })}
        />
      ) : null}

      {draft.payCycle === 'semimonthly' ? (
        <SemimonthlyPicker
          first={draft.payDay}
          second={draft.payDay2 ?? 30}
          onChange={(first, second) =>
            setDraft({ payDay: first, payDay2: second })
          }
        />
      ) : null}

      {draft.payCycle === 'monthly' ? (
        <DropdownSelect
          options={dayOfMonthOptions}
          value={String(draft.payDay)}
          onChange={(v) => setDraft({ payDay: Number(v) })}
        />
      ) : null}
    </StepLayout>
  );
}

function WeeklyPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (dow: number) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Day of the week"
      className="grid grid-cols-7 gap-2"
    >
      {DOW_LABELS.map((letter, i) => {
        const active = value === i;
        return (
          <motion.button
            key={i}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={DOW_FULL[i]}
            onClick={() => onChange(i)}
            whileTap={tapScale}
            transition={springQuick}
            className={cn(
              'h-12 rounded-button text-[15px] font-medium',
              active
                ? 'bg-accent text-white shadow-md'
                : 'bg-surface-secondary text-text-primary',
            )}
          >
            {letter}
          </motion.button>
        );
      })}
    </div>
  );
}

function BiweeklyPicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (iso: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-caption uppercase text-text-tertiary mb-2 block">
        Most recent payday
      </span>
      <input
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'w-full h-12 px-4 rounded-button bg-surface-secondary',
          'text-[16px] text-text-primary',
          'focus:outline-none focus:ring-2 focus:ring-accent/40',
        )}
      />
    </label>
  );
}

function SemimonthlyPicker({
  first,
  second,
  onChange,
}: {
  first: number;
  second: number;
  onChange: (first: number, second: number) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-card-gap">
      <label className="block">
        <span className="text-caption uppercase text-text-tertiary mb-2 block">
          First
        </span>
        <DropdownSelect
          options={dayOfMonthOptions}
          value={String(first)}
          onChange={(v) => onChange(Number(v), second)}
        />
      </label>
      <label className="block">
        <span className="text-caption uppercase text-text-tertiary mb-2 block">
          Second
        </span>
        <DropdownSelect
          options={dayOfMonthOptions}
          value={String(second)}
          onChange={(v) => onChange(first, Number(v))}
        />
      </label>
    </div>
  );
}
